import React, { useState, useEffect, useRef, useMemo } from "react";
import { GoogleMap, Marker, Circle, InfoWindow, Polyline } from "@react-google-maps/api";
import useLoadGoogleMaps from "./useLoadGoogleMaps";
import GroundOverlay from "./GroundOverlay";
import axios from 'axios';
import "bootstrap/dist/css/bootstrap.min.css"; // Bootstrap for accordion
import "./MapComponent.css"; // Custom CSS file
import debounce from "lodash.debounce";
import SearchInfoWindow from "./SearchInfoWindow";
import { useNavigate } from 'react-router-dom';


const LIBRARIES = ["drawing", "geometry"];


const createApiInstance = (token, backendKey) => {
  return axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-api-key': backendKey,
      'Content-Type': 'application/json'
    },
    withCredentials: true // ensures cookies (refresh token) are sent
  });
};

const MapComponent = ({ accessToken, onLogout }) => {

  const API_URL = process.env.REACT_APP_API_URL;
  const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
  const BACKEND_KEY = process.env.REACT_APP_BACKEND_KEY;

  const initialState = {
    visibleLayers: {
      dataCentres: false,
      powerPlants: false,
      substations: false,
      powerLines: false,
      landingstations: false,
      internetExchanges: false,
      airports: false
    },
    filterCategory: "",
    filterSize: {
      size_live: { min: 0, max: null },
      size_under_construction: { min: 0, max: null },
      size_design: { min: 0, max: null },
    },
    searchQuery: "",
    defaultLocation: { lat: 24.7136, lng: 46.6753 },
    zoomLevel: 4,
    selectedCentre: null,
    selectedShape: null, // New state for selected shape
    showMoreCompanies: false, // New state for "Show More"
  };

  const [layers, setLayers] = useState({
    dataCentres: [],
    powerPlants: [],
    substations: [],
    powerLines: null,
    landingstations: [],
    internetExchanges: [],
    airports: [],
  });

  const api = useMemo(
    () => createApiInstance(accessToken, BACKEND_KEY),
    [accessToken, BACKEND_KEY]
  );

  const mapRef = useRef(null);
  const navigate = useNavigate();
  const [showZoomHint, setShowZoomHint] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [measurementShapes, setMeasurementShapes] = useState([]);
  const [visibleLayers, setVisibleLayers] = useState(initialState.visibleLayers);
  const [filterCategory, setFilterCategory] = useState(initialState.filterCategory);
  const [filterSize, setFilterSize] = useState(initialState.filterSize);
  const [searchQuery, setSearchQuery] = useState(initialState.searchQuery);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCentre, setSelectedCentre] = useState(initialState.selectedCentre);
  const [selectedShape, setSelectedShape] = useState(initialState.selectedShape);
  const [showMoreCompanies, setShowMoreCompanies] = useState(initialState.showMoreCompanies);
  const [userLocation, setUserLocation] = useState(initialState.defaultLocation);
  const [zoomLevel, setZoomLevel] = useState(initialState.zoomLevel);
  const [drawingManager, setDrawingManager] = useState(null);
  const [currentBounds, setCurrentBounds] = useState(null); // Track bounding box
  const [currentZoom, setCurrentZoom] = useState(null);     // Track zoom level
  const [companySearch, setCompanySearch] = useState(""); // Tracks the user's input
  const [companySuggestions, setCompanySuggestions] = useState([]); // Tracks autocomplete suggestions
  const [selectedCompany, setSelectedCompany] = useState(null); // Tracks the selected company
  const [originalDataCentres, setOriginalDataCentres] = useState([]); // Store the original dataset
  const [selectedPlant, setSelectedPlant] = useState(null); // Tracks the selected individual plant
  const [mapKey, setMapKey] = useState(1); // A key to force map reload
  const [drawnShapes, setDrawnShapes] = useState([]); // Track drawn shapes
  const [selectedSubstation, setSelectedSubstation] = useState(null);
  const [selectedInternetExchange, setSelectedInternetExchange] = useState(null);
  const [selectedLandingStation, setSelectedLandingStation] = useState(null);
  const [selectedAirport, setSelectedAirport] = useState(null);
  const [currentSubstationBounds, setCurrentSubstationBounds] = useState(null); // Track bounding box for substations
  const [substationCache, setSubstationCache] = useState({}); // Cache for substations
  const [searchSelectedCentre, setSearchSelectedCentre] = useState(null);
  const [selectedPowerLine, setSelectedPowerLine] = useState(null);
  const powerLineDetailsCacheRef = useRef({});
  const powerLinesCacheRef = useRef({});
  const lastPLKeyRef = useRef(null);
  const activePowerLineIdRef = useRef(null);
  const [powerLineLoading, setPowerLineLoading] = useState(false);

  const { loaded: mapsLoaded, error: mapsError } = useLoadGoogleMaps({ apiKey: API_KEY, libraries: LIBRARIES });

  //get user location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.warn("Could not fetch location, using default.", error);
          setUserLocation(initialState.defaultLocation); // Explicitly set fallback
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    } else {
      console.warn("Geolocation is not supported by this browser, using default.");
      setUserLocation(initialState.defaultLocation); // Explicitly set fallback
    }
  }, []);

  // Fetch substations based on bounding box
  const fetchSubstations = async (params) => {
    const { latMin, latMax, lonMin, lonMax, zoom } = params;

    // Generate a unique cache key based on bounding box
    const cacheKey = `${latMin.toFixed(5)}-${latMax.toFixed(5)}-${lonMin.toFixed(5)}-${lonMax.toFixed(5)}`;

    // Check if the result is already in the cache
    if (cacheRef.current[cacheKey]) {
      console.log(`Using cached data for bounding box ${cacheKey}`);
      setLayers((prev) => ({ ...prev, substations: cacheRef.current[cacheKey] }));
      return;
    }

    // Fetch new data if not cached
    try {
      const response = await api.get(`/substations`, {
        params
      });
      cacheRef.current[cacheKey] = response.data; // Store result in cache
      setLayers((prev) => ({ ...prev, substations: response.data }));
    } catch (error) {
      console.error("Error fetching substations:", error);
    }
  };
  // Debounced fetch function for substations
  const debouncedFetchSubstations = debounce(fetchSubstations, 500);

  // Substations layer effect
  useEffect(() => {
    const mapInstance = mapRef.current;

    if (mapInstance && visibleLayers.substations) {
      const zoom = mapInstance.getZoom();
      const minimumZoomForSubstations = 10;

      if (zoom < minimumZoomForSubstations) {
        // Show hint
        setShowZoomHint(true);
        const timeout = setTimeout(() => setShowZoomHint(false), 3000);

        // Clear substation layer from map
        setLayers((prev) => ({ ...prev, substations: [] }));

        return () => clearTimeout(timeout);
      }

      // Hide hint
      setShowZoomHint(false);

      const bounds = mapInstance.getBounds();
      const newBounds = {
        latMin: bounds.getSouthWest().lat(),
        latMax: bounds.getNorthEast().lat(),
        lonMin: bounds.getSouthWest().lng(),
        lonMax: bounds.getNorthEast().lng(),
      };

      // Fetch if bounds or zoom have changed significantly
      if (
        !currentSubstationBounds ||
        currentZoom !== zoom ||
        Math.abs(currentSubstationBounds.latMin - newBounds.latMin) > 0.1 ||
        Math.abs(currentSubstationBounds.latMax - newBounds.latMax) > 0.1 ||
        Math.abs(currentSubstationBounds.lonMin - newBounds.lonMin) > 0.1 ||
        Math.abs(currentSubstationBounds.lonMax - newBounds.lonMax) > 0.1
      ) {
        setCurrentSubstationBounds(newBounds);
        setCurrentZoom(zoom);
        debouncedFetchSubstations({ ...newBounds, zoom });
      }
    }
  }, [zoomLevel, userLocation, visibleLayers.substations]);


  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [
          dataCentresRes,
          landingStationsRes,
          airportsRes,
          internetExchangesRes,
        ] = await Promise.all([
          api.get("/data-centres", { headers: { "x-api-key": BACKEND_KEY } }),
          api.get("/landing-stations", { headers: { "x-api-key": BACKEND_KEY } }),
          api.get("/airports", { headers: { "x-api-key": BACKEND_KEY } }),
          api.get("/internet-exchanges", { headers: { "x-api-key": BACKEND_KEY } }),
        ]);

        const dataCentres = (dataCentresRes.data || []).map(c => ({
          ...c,
          longitude: Number(c.longitude),
          latitude: Number(c.latitude),
        }));

        setLayers((prev) => ({
          ...prev,
          dataCentres,
          landingStations: landingStationsRes.data,
          airports: airportsRes.data,
          internetExchanges: internetExchangesRes.data,
        }));

        setOriginalDataCentres(dataCentres);
        console.log("✅ All layers fetched:", {
          dataCentres: dataCentres.length,
          landing: landingStationsRes.data.length,
          airports: airportsRes.data.length,
          ix: internetExchangesRes.data.length,
        });
      } catch (err) {
        console.error("Error fetching layers:", err);
      }
    };

    fetchAll();
  }, []);

  const handleSearch = (query) => {
    setSearchQuery(query);

    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const results = filteredDataCentres.filter((centre) => {
      const lowerQuery = query.toLowerCase();
      return (
        centre.sitename.toLowerCase().includes(lowerQuery) ||
        (centre.company && centre.company.toLowerCase().includes(lowerQuery))
      );
    });

    setSearchResults(results);
  };

  const handleSelect = (centre) => {
    const current = searchSelectedCentre;
    if (current?.id === centre.id) return; // Prevent setting the same thing

    setSelectedCentre(null);
    setSelectedPlant(null);
    setSelectedShape(null);
    setSearchSelectedCentre(centre); // Only update if it really changed
    setUserLocation({
      lat: parseFloat(centre.latitude),
      lng: parseFloat(centre.longitude),
    });
    setZoomLevel(15);
    setSearchQuery("");
    setSearchResults([]);
  };


  const onLoad = (mapInstance) => {
    // This function gets called once the map is loaded
    mapRef.current = mapInstance;
    // Log the map instance to verify it's the correct object
    console.log('Map instance:', mapInstance);
    const drawingManagerInstance = new window.google.maps.drawing.DrawingManager({
      drawingMode: null,
      drawingControl: false,
    });


    drawingManagerInstance.setMap(mapInstance);
    setDrawingManager(drawingManagerInstance);

  };

  const onMapIdle = () => {
    const mapInstance = mapRef.current;
    if (mapInstance) {
      const center = mapInstance.getCenter();
      if (center) {
        setUserLocation({ lat: center.lat(), lng: center.lng() });
      }
      const bounds = mapInstance.getBounds();
      if (bounds) {
        setCurrentBounds({
          latMin: bounds.getSouthWest().lat(),
          latMax: bounds.getNorthEast().lat(),
          lonMin: bounds.getSouthWest().lng(),
          lonMax: bounds.getNorthEast().lng(),
        });
      }
      setCurrentZoom(mapInstance.getZoom());
    }
  };


  const cacheRef = useRef({});

  const fetchPowerPlants = async (params) => {
    const { latMin, latMax, lonMin, lonMax, zoom } = params;

    // Generate a unique cache key based on bounding box
    const cacheKey = `${latMin.toFixed(5)}-${latMax.toFixed(5)}-${lonMin.toFixed(5)}-${lonMax.toFixed(5)}`;

    // Check if the result is already in the cache
    if (cacheRef.current[cacheKey]) {
      console.log(`Using cached data for bounding box ${cacheKey}`);
      setLayers((prev) => ({ ...prev, powerPlants: cacheRef.current[cacheKey] }));
      return;
    }

    // Fetch new data if not cached
    try {
      const response = await api.get(`/power-plants`, {
        params
      });
      cacheRef.current[cacheKey] = response.data; // Store result in cache
      setLayers((prev) => ({ ...prev, powerPlants: response.data }));
    } catch (error) {
      console.error("Error fetching power plants:", error);
    }
  };

  // Helper function to fetch substation details
  const fetchSubstationDetails = async (substationIds) => {
    if (!Array.isArray(substationIds) || substationIds.length === 0) {
      console.warn("fetchSubstationDetails called with invalid or empty IDs:", substationIds);
      return [];
    }

    const ids = substationIds
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0);

    if (ids.length === 0) {
      console.warn("No valid substation IDs to fetch:", substationIds);
      return [];
    }

    try {
      const response = await api.get('/substations/details', {
        params: { ids },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching substation details:', error);
      return [];
    }
  };

  // Helper function to fetch plant details
  const fetchPlantDetails = async (plantIds) => {
    let ids = Array.isArray(plantIds)
      ? plantIds
      : [plantIds];

    // Ensure values are numbers and filter out invalid entries
    ids = ids
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0);

    if (ids.length === 0) {
      console.warn("fetchPlantDetails called with invalid or empty IDs:", plantIds);
      throw new Error("No valid plant IDs provided");
    }

    try {
      const response = await api.get('/power-plants/details', {
        params: { ids },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching power plant details:', error);
      throw error;
    }
  };

  // Power plants layer effect
  useEffect(() => {
    const mapInstance = mapRef.current;

    if (mapInstance && visibleLayers.powerPlants) {
      const zoom = mapInstance.getZoom();
      const minimumZoomForPowerPlants = 10;

      if (zoom < minimumZoomForPowerPlants) {
        // Show hint
        setShowZoomHint(true);
        const timeout = setTimeout(() => setShowZoomHint(false), 3000);

        // Clear substation layer from map
        setLayers((prev) => ({ ...prev, powerPlants: [] }));

        return () => clearTimeout(timeout);
      }

      // Hide hint
      setShowZoomHint(false);

      const bounds = mapInstance.getBounds();

      const newBounds = {
        latMin: bounds.getSouthWest().lat(),
        latMax: bounds.getNorthEast().lat(),
        lonMin: bounds.getSouthWest().lng(),
        lonMax: bounds.getNorthEast().lng(),
      };

      // Only fetch if bounds or zoom have changed significantly
      if (
        !currentBounds ||
        currentZoom !== zoom ||
        Math.abs(currentBounds.latMin - newBounds.latMin) > 0.1 ||
        Math.abs(currentBounds.latMax - newBounds.latMax) > 0.1 ||
        Math.abs(currentBounds.lonMin - newBounds.lonMin) > 0.1 ||
        Math.abs(currentBounds.lonMax - newBounds.lonMax) > 0.1
      ) {
        setCurrentBounds(newBounds);
        setCurrentZoom(zoom);
        debouncedFetchPowerPlants({ ...newBounds, zoom });
      }
    }
  }, [zoomLevel, userLocation, visibleLayers.powerPlants]); // Dependencies

  const debouncedFetchPowerPlants = debounce(fetchPowerPlants, 500);

  const handleCompanySearch = (e) => {
    const searchQuery = e.target.value;
    setCompanySearch(searchQuery);

    // Explicitly clear suggestions if input is empty
    if (searchQuery.trim() === "") {
      setCompanySuggestions([]);
      return;
    }


    const lowerQuery = searchQuery.toLowerCase();

    // Get filtered suggestions based on current input
    const filteredSuggestions = (layers.dataCentres || [])
      .map((centre) => centre.company)
      .filter(
        (company, index, self) =>
          company &&
          company.toLowerCase().includes(lowerQuery) &&
          self.indexOf(company) === index
      );

    if (filteredSuggestions.length > 0) {
      // Add contextually relevant "All" option at the top
      setCompanySuggestions(["All", ...filteredSuggestions.slice(0, 9)]);
    } else {
      setCompanySuggestions([]);
    }
  };

  const handleCompanySelect = (company) => {
    if (company === "All") {
      const lowerQuery = companySearch.toLowerCase();

      const matchingCompanies = (layers.dataCentres || [])
        .map((centre) => centre.company)
        .filter(
          (c, index, self) =>
            c &&
            c.toLowerCase().includes(lowerQuery) &&
            self.indexOf(c) === index
        );

      setSelectedCompany(matchingCompanies); // An array of matching companies
    } else {
      setSelectedCompany([company]); // Make selectedCompany an array for consistency
    }

    setCompanySearch(company);
    setCompanySuggestions([]);
  };

  useEffect(() => {
    if (companySearch === "") {
      setSelectedCompany(null); // Clear filter when search is empty
    }
  }, [companySearch]);


  const filteredDataCentres = useMemo(() => {
    return (originalDataCentres || []).filter((centre) => {
      // Category filter
      const matchesCategory = !filterCategory || centre.category === filterCategory;

      // Size filters
      const matchesSizeLive =
        (!filterSize.size_live.min || (centre.size_l ?? 0) >= parseFloat(filterSize.size_live.min)) &&
        (!filterSize.size_live.max || (centre.size_l ?? 0) <= parseFloat(filterSize.size_live.max));

      const matchesSizeUnderConstruction =
        (!filterSize.size_under_construction.min || (centre.size_u ?? 0) >= parseFloat(filterSize.size_under_construction.min)) &&
        (!filterSize.size_under_construction.max || (centre.size_u ?? 0) <= parseFloat(filterSize.size_under_construction.max));

      const matchesSizeDesign =
        (!filterSize.size_design.min || (centre.size_d ?? 0) >= parseFloat(filterSize.size_design.min)) &&
        (!filterSize.size_design.max || (centre.size_d ?? 0) <= parseFloat(filterSize.size_design.max));

      // Company filter
      const matchesCompany =
        !selectedCompany ||
        (Array.isArray(selectedCompany)
          ? selectedCompany.includes(centre.company)
          : centre.company &&
          centre.company.toLowerCase() === selectedCompany.toLowerCase());

      return matchesCategory && matchesSizeLive && matchesSizeUnderConstruction && matchesSizeDesign && matchesCompany;
    });
  }, [originalDataCentres, filterCategory, filterSize, selectedCompany]);


  // useEffect(() => {
  //   const filteredData = getFilteredDataCentres();
  //   setLayers((prev) => ({
  //     ...prev,
  //     dataCentres: filteredData,
  //   }));
  // }, [filterCategory, filterSize, selectedCompany, originalDataCentres]);


  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && drawingManager) {
        drawingManager.setDrawingMode(null); // Disable drawing mode
        setIsMeasuring(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown); // Clean up on unmount
    };
  }, [drawingManager]);



  const enableDrawing = (shapeType) => {
    if (!drawingManager) return;

    setIsMeasuring(false);

    let mode = null;
    if (shapeType === "circle") mode = window.google.maps.drawing.OverlayType.CIRCLE;
    if (shapeType === "rectangle") mode = window.google.maps.drawing.OverlayType.RECTANGLE;
    if (shapeType === "polygon") mode = window.google.maps.drawing.OverlayType.POLYGON;

    drawingManager.setDrawingMode(mode);
  };

  const enableMeasurement = (measurementType) => {
    if (!drawingManager) return;

    setIsMeasuring(true);

    let mode = null;
    if (measurementType === "polygon") {
      mode = window.google.maps.drawing.OverlayType.POLYGON;
    } else if (measurementType === "polyline") {
      mode = window.google.maps.drawing.OverlayType.POLYLINE;
    }

    drawingManager.setDrawingMode(mode);
  };

  const removeDrawnShapes = () => {
    drawnShapes.forEach((shape) => shape.setMap(null));
    setDrawnShapes([]);

    measurementShapes.forEach((shape) => shape.setMap(null));
    setMeasurementShapes([]);
  };



  useEffect(() => {
    if (!drawingManager) return;

    // Clear all previous listeners to prevent duplicates
    window.google.maps.event.clearListeners(drawingManager, "overlaycomplete");

    const handleOverlayComplete = async (event) => {
      const shape = event.overlay;
      const type = event.type;

      if (isMeasuring) {
        // --- Measurement Mode ---
        if (type !== "polygon" && type !== "polyline") return;

        let infoPosition = null;
        let content = "";

        if (type === "polygon") {
          const path = shape.getPath();
          const area = window.google.maps.geometry.spherical.computeArea(path);
          infoPosition = path.getAt(0);
          content = `<strong>Area:</strong> ${area.toFixed(2)} m²`;
        } else if (type === "polyline") {
          const path = shape.getPath();
          let distance = 0;
          for (let i = 0; i < path.getLength() - 1; i++) {
            distance += window.google.maps.geometry.spherical.computeDistanceBetween(
              path.getAt(i),
              path.getAt(i + 1)
            );
          }
          infoPosition = path.getAt(0);
          content = `<strong>Distance:</strong> ${distance.toFixed(2)} m`;
        }

        const infoWindow = new window.google.maps.InfoWindow({
          content,
          position: infoPosition,
        });

        infoWindow.open(mapRef.current);
        shape.addListener("click", () => infoWindow.open(mapRef.current));

        setMeasurementShapes((prev) => [...prev, shape]);
        drawingManager.setDrawingMode(null);
        return;
      }

      // --- Drawing Mode ---
      setLoading(true);
      try {
        const newShape = event.overlay;
        setDrawnShapes((prevShapes) => [...prevShapes, newShape]);

        let shapeDetails;
        let infoPosition;

        if (type === "polygon") {
          const path = newShape.getPath();
          const coordinates = [];
          path.forEach((latLng) => {
            coordinates.push({ lat: latLng.lat(), lng: latLng.lng() });
          });
          shapeDetails = { type: "polygon", coordinates };
          infoPosition = coordinates[0];
        } else if (type === "circle") {
          const center = newShape.getCenter();
          const radius = newShape.getRadius();
          shapeDetails = {
            type: "circle",
            center: { lat: center.lat(), lng: center.lng() },
            radius,
          };
          infoPosition = { lat: center.lat(), lng: center.lng() };
        } else if (type === "rectangle") {
          const bounds = newShape.getBounds();
          shapeDetails = {
            type: "rectangle",
            bounds: {
              north: bounds.getNorthEast().lat(),
              east: bounds.getNorthEast().lng(),
              south: bounds.getSouthWest().lat(),
              west: bounds.getSouthWest().lng(),
            },
          };
          infoPosition = {
            lat: (bounds.getNorthEast().lat() + bounds.getSouthWest().lat()) / 2,
            lng: (bounds.getNorthEast().lng() + bounds.getSouthWest().lng()) / 2,
          };
        }

        const filterByShape = (lat, lng) => {
          const point = new window.google.maps.LatLng(lat, lng);
          if (shapeDetails.type === "circle") {
            return (
              window.google.maps.geometry.spherical.computeDistanceBetween(
                point,
                newShape.getCenter()
              ) <= shapeDetails.radius
            );
          } else if (shapeDetails.type === "polygon") {
            return window.google.maps.geometry.poly.containsLocation(point, newShape);
          } else if (shapeDetails.type === "rectangle") {
            const { north, south, east, west } = shapeDetails.bounds;
            return lat >= south && lat <= north && lng >= west && lng <= east;
          }
          return false;
        };

        const dataCentresInShape = (filteredDataCentres || []).filter((centre) =>
          filterByShape(parseFloat(centre.latitude), parseFloat(centre.longitude))
        );

        const filteredPowerPlants = (layers.powerPlants || []).filter((plant) =>
          filterByShape(parseFloat(plant.latitude), parseFloat(plant.longitude))
        );


        const fetchPlantDetails = async (plantIds) => {
          try {
            const response = await api.get(`/power-plants/details`, {
              params: { ids: plantIds },
              paramsSerializer: (params) =>
                new URLSearchParams(params.ids.map((id) => ["ids[]", id])).toString(),
            });
            return response.data;
          } catch (error) {
            console.error("Error fetching plant details:", error);
            return [];
          }
        };

        const expandedPowerPlants = (
          await Promise.all(
            filteredPowerPlants.map(async (plantGroup) =>
              plantGroup.plant_ids ? await fetchPlantDetails(plantGroup.plant_ids) : [plantGroup]
            )
          )
        ).flat();

        let validCount = 0;
        const totalOutput = expandedPowerPlants
          .reduce((sum, plant) => {
            const output = Number(plant.capacity_mw);
            if (isNaN(output)) return sum;
            validCount++;
            return sum + output;
          }, 0)
          .toFixed(2);

        const powerPlantsData = {
          category: "Power Plants",
          totalOutput,
          contributingPlants: validCount,
          numberOfPlants: expandedPowerPlants.length,
          methods: Array.from(
            new Set(expandedPowerPlants.map((plant) => plant.primary_fuel).filter(Boolean))
          ),
        };

        const dataCentresData = {
          category: "Data Centres",
          rows: ["Self-build", "Colocation"].map((category) => ({
            category,
            count: dataCentresInShape.filter((c) => c.category === category).length,
            live: dataCentresInShape
              .filter((c) => c.category === category)
              .reduce((sum, c) => sum + c.size_l, 0),
            underConstruction: dataCentresInShape
              .filter((c) => c.category === category)
              .reduce((sum, c) => sum + c.size_u, 0),
            committed: dataCentresInShape
              .filter((c) => c.category === category)
              .reduce((sum, c) => sum + c.size_d, 0),
          })).concat([
            {
              category: "Total",
              count: dataCentresInShape.length,
              live: dataCentresInShape.reduce((sum, c) => sum + c.size_l, 0),
              underConstruction: dataCentresInShape.reduce((sum, c) => sum + c.size_u, 0),
              committed: dataCentresInShape.reduce((sum, c) => sum + c.size_d, 0),
            },
          ]),
          companies: Array.from(
            new Set(dataCentresInShape.map((c) => c.company).filter(Boolean))
          ),
        };

        const shapePayload = {
          details: shapeDetails,
          position: infoPosition,
          dataCentres: visibleLayers.dataCentres ? dataCentresData : null,
          powerPlants: visibleLayers.powerPlants ? powerPlantsData : null,
          shape: newShape,
        };

        setSelectedShape(shapePayload);
        drawingManager.setDrawingMode(null);
      } catch (err) {
        console.error("Error in overlaycomplete handler:", err);
      } finally {
        setLoading(false);
      }
    };

    const listener = window.google.maps.event.addListener(
      drawingManager,
      "overlaycomplete",
      handleOverlayComplete
    );

    return () => {
      window.google.maps.event.removeListener(listener);
    };
  }, [drawingManager, mapRef, isMeasuring, layers.dataCentres, layers.powerPlants, visibleLayers, filteredDataCentres]);



  useEffect(() => {
    if (!selectedShape) return;

    let updated = false;
    const updatedShape = { ...selectedShape };

    // Clear dataCentres if it's hidden
    if (!visibleLayers.dataCentres && selectedShape.dataCentres) {
      updatedShape.dataCentres = null;
      updated = true;
    }

    // Clear powerPlants if it's hidden
    if (!visibleLayers.powerPlants && selectedShape.powerPlants) {
      updatedShape.powerPlants = null;
      updated = true;
    }

    // If all layer data is now hidden, remove the InfoWindow entirely
    if (!visibleLayers.dataCentres && !visibleLayers.powerPlants) {
      setSelectedShape(null);
      return;
    }

    // Only update shape if something actually changed
    if (updated) {
      setSelectedShape(updatedShape);
    }
  }, [visibleLayers, selectedShape]);


  const resetMap = () => {
    setMapKey((prevKey) => prevKey + 1); // Increment the key to reload the map
    setVisibleLayers(initialState.visibleLayers);
    setFilterCategory(initialState.filterCategory);
    setFilterSize(initialState.filterSize);
    setSearchQuery(initialState.searchQuery);
    setSelectedCentre(initialState.selectedCentre);
    setSelectedShape(null);
    setShowMoreCompanies(false);
    setZoomLevel(initialState.zoomLevel);
    setSelectedCompany(null);
    setCompanySearch("");
    removeDrawnShapes(); // Clear all drawn shapes

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.warn("Could not fetch location, using default.", error);
          setUserLocation(initialState.defaultLocation); // Explicitly set fallback
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    } else {
      console.warn("Geolocation is not supported by this browser, using default.");
      setUserLocation(initialState.defaultLocation); // Explicitly set fallback
    }
  };


  const mapContainerStyle = {
    flex: 1,
    width: "100%",
    height: "100%" // Let parent CSS (flex-grow) dictate height
  };


  const center = {
    lat: 25.0,
    lng: 45.0
  };

  const saudiBounds = {
    north: 32.161009,
    south: 16.347891,
    east: 55.666659,
    west: 34.632336
  };

  const ukBounds = {
    north: 59.4,     // Previously 59.5
    south: 49.9,     // Previously 50.0
    west: -8.25,
    east: 2.0
  };


  const geojsonToPowerLineArray = (fc) => {
    if (!fc || fc.type !== 'FeatureCollection') return [];
    const out = [];
    const toPath = (coords) => coords.map(([lng, lat]) => ({ lat, lng }));

    for (const feat of fc.features || []) {
      const id = feat.properties?.id ?? feat.id ?? feat.properties?.ogc_fid ?? null;
      const g = feat.geometry;
      if (!g || !id) continue;
      const voltage_kv = feat.properties?.voltage_kv ?? null;

      if (g.type === 'LineString') {
        out.push({ key: `${id}-0`, id, path: toPath(g.coordinates), voltage_kv });
      } else if (g.type === 'MultiLineString') {
        g.coordinates.forEach((line, i) => {
          out.push({ key: `${id}-${i}`, id, path: toPath(line), voltage_kv });
        });
      }
    }
    return out;
  };


  const fetchPowerLines = async ({ latMin, latMax, lonMin, lonMax, zoom }) => {
    const key = `pl:${zoom}:${latMin.toFixed(5)}:${latMax.toFixed(5)}:${lonMin.toFixed(5)}:${lonMax.toFixed(5)}`;
    if (powerLinesCacheRef.current[key]) {
      setLayers(prev => ({ ...prev, powerLines: powerLinesCacheRef.current[key] }));
      return;
    }
    try {
      const { data } = await api.get('/power-lines', { params: { latMin, latMax, lonMin, lonMax, zoom } });
      const arr = geojsonToPowerLineArray(data);
      powerLinesCacheRef.current[key] = arr;
      setLayers(prev => ({ ...prev, powerLines: arr }));
    } catch (err) {
      console.error('Error fetching power lines:', err);
    }
  };

  const debouncedFetchPowerLines = debounce(fetchPowerLines, 500);

  const fetchPowerLineDetails = async (id) => {
    if (powerLineDetailsCacheRef.current[id]) return powerLineDetailsCacheRef.current[id];
    try {
      const { data } = await api.get('/power-lines/details', { params: { ids: [id] } });
      const row = Array.isArray(data) ? data.find(d => d.id === id) : null;
      if (row) powerLineDetailsCacheRef.current[id] = row;
      return row ?? null;
    } catch (err) {
      console.error('Error fetching power line details:', err);
      return null;
    }
  };


  useEffect(() => {
    if (!visibleLayers.powerLines || !currentBounds || currentZoom == null) return;

    const minZoom = 10;

    if (currentZoom < minZoom) {
      setShowZoomHint(true);
      const timeout = setTimeout(() => setShowZoomHint(false), 3000);

      // Clear the layer while below threshold
      setLayers(prev => ({ ...prev, powerLines: [] }));

      // Optional: reset the last key so we refetch immediately after zooming back in
      // lastPLKeyRef.current = null;

      // Clean up the timer if dependencies change or component unmounts
      return () => clearTimeout(timeout);
    }

    // At/above threshold: hide the hint and proceed to fetch
    setShowZoomHint(false);

    const key = `pl:${currentZoom}:${currentBounds.latMin.toFixed(5)}:${currentBounds.latMax.toFixed(5)}:${currentBounds.lonMin.toFixed(5)}:${currentBounds.lonMax.toFixed(5)}`;
    if (lastPLKeyRef.current === key) return;
    lastPLKeyRef.current = key;

    debouncedFetchPowerLines({ ...currentBounds, zoom: currentZoom });
  }, [visibleLayers.powerLines, currentBounds, currentZoom]);

  // Close window if layer is toggled off
  useEffect(() => {
    if (!visibleLayers.powerLines) setSelectedPowerLine(null);
  }, [visibleLayers.powerLines]);


  return (
    <div id="app" class="parent-container">
      <div class="map-container">
        <header className="row bg-secondary text-white p-3">
          <div className="col">
            <h3>
              <span style={{ color: '#97B464', fontFamily: 'Arial Black, sans-serif', fontSize: '2rem' }}>DC</span>
              <span style={{ color: 'white', fontFamily: 'Arial, sans-serif' }}>Nav</span>
            </h3>
          </div>

          <div className="col">
            <input
              type="text"
              className="form-control"
              placeholder="Search by name or company..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
            {searchResults.length > 0 && (
              <ul
                className="list-group position-absolute mt-1 overflow-auto"
                style={{ zIndex: 1000, maxHeight: "400px", width: "44%" }}
              >
                {searchResults.map((result) => (
                  <li
                    key={result.id}
                    className="list-group-item list-group-item-action"
                    onClick={() => handleSelect(result)}
                    style={{ cursor: "pointer" }}
                  >
                    <strong>{result.sitename}</strong> - {result.company || "N/A"}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Reset Button */}
          <div
            className="col-auto"
            onClick={resetMap}
            style={{ cursor: "pointer", marginTop: "10px", marginRight: "10px" }}
          >
            <i
              className="fas fa-undo"
              style={{ fontSize: "24px", color: "#97B464" }}
              title="Reset"
            ></i>
          </div>

          {/* Staging Interface */}
          <div
            className="col-auto"
            onClick={() => navigate('/staging', { state: { accessToken } })}
            style={{ cursor: 'pointer', marginTop: '10px' }}
            title="Staging Interface"
          >
            <i
              className="fas fa-map"
              style={{ fontSize: '24px', color: '#20c997' }}
            ></i>
          </div>

          {/* Csv Uploader */}
          <div
            className="col-auto"
            onClick={() => navigate('/upload', { state: { accessToken } })}
            style={{ cursor: 'pointer', marginTop: '10px' }}
            title="Upload CSV"
          >
            <i
              className="fas fa-upload"
              style={{ fontSize: '24px', color: '#0d6efd' }}
            ></i>
          </div>

          {/* Logout Button */}
          <div
            className="col-auto"
            onClick={onLogout}
            style={{ cursor: "pointer", marginTop: "10px" }}
          >
            <i
              className="fas fa-sign-out-alt"
              style={{ fontSize: "24px", color: "#dc3545" }}
              title="Logout"
            ></i>
          </div>
        </header>


        <div className="row h-100">
          <aside className="col-md-3 col-lg-2 bg-light p-3 border-end position-relative">
            <div className="accordion" id="mainAccordion">
              <div className="accordion-item">
                <h2 className="accordion-header" id="headingLayers">
                  <button
                    className="accordion-button"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseLayers"
                    aria-expanded="true"
                    aria-controls="collapseLayers"
                  >
                    Layers
                  </button>
                </h2>
                <div
                  id="collapseLayers"
                  className="accordion-collapse collapse show"
                  aria-labelledby="headingLayers"
                  data-bs-parent="#mainAccordion"
                >
                  <div className="accordion-body">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="dataCentres"
                        checked={visibleLayers.dataCentres}
                        onChange={() =>
                          setVisibleLayers((prev) => ({
                            ...prev,
                            dataCentres: !prev.dataCentres,
                          }))
                        }
                      />
                      <label className="form-check-label" htmlFor="dataCentres">
                        Data Centres
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="powerPlants"
                        checked={visibleLayers.powerPlants}
                        onChange={() =>
                          setVisibleLayers((prev) => ({
                            ...prev,
                            powerPlants: !prev.powerPlants,
                          }))
                        }
                      />
                      <label className="form-check-label" htmlFor="powerPlants">
                        Power Plants
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="substations"
                        checked={visibleLayers.substations}
                        onChange={() =>
                          setVisibleLayers((prev) => ({
                            ...prev,
                            substations: !prev.substations,
                          }))
                        }
                      />
                      <label className="form-check-label" htmlFor="substations">
                        Substations
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="powerLines"
                        checked={visibleLayers.powerLines}
                        onChange={() =>
                          setVisibleLayers(prev => ({ ...prev, powerLines: !prev.powerLines }))
                        }
                      />
                      <label className="form-check-label" htmlFor="powerLines">
                        Power Lines
                      </label>
                    </div>

                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="landingstations"
                        checked={visibleLayers.landingstations}
                        onChange={() =>
                          setVisibleLayers((prev) => ({
                            ...prev,
                            landingstations: !prev.landingstations,
                          }))
                        }
                      />
                      <label className="form-check-label" htmlFor="landingstations">
                        Landing Stations
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="internetExchanges"
                        checked={visibleLayers.internetExchanges}
                        onChange={() =>
                          setVisibleLayers((prev) => ({
                            ...prev,
                            internetExchanges: !prev.internetExchanges,
                          }))
                        }
                      />
                      <label className="form-check-label" htmlFor="internetExchanges">
                        Internet Exchanges
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="airports"
                        checked={visibleLayers.airports}
                        onChange={() =>
                          setVisibleLayers((prev) => ({
                            ...prev,
                            airports: !prev.airports,
                          }))
                        }
                      />
                      <label className="form-check-label" htmlFor="airports">
                        Airports
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="tempMasks"
                        checked={visibleLayers.tempMasks}
                        onChange={() =>
                          setVisibleLayers((prev) => ({
                            ...prev,
                            tempMasks: !prev.tempMasks,
                          }))
                        }
                      />
                      <label className="form-check-label" htmlFor="tempMasks">
                        Heat Masks
                      </label>
                    </div>

                  </div>
                </div>
              </div>

              <div className="accordion-item">
                <h2 className="accordion-header" id="headingFilters">
                  <button
                    className="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseFilters"
                    aria-expanded="false"
                    aria-controls="collapseFilters"
                  >
                    Filters
                  </button>
                </h2>
                <div
                  id="collapseFilters"
                  className="accordion-collapse collapse"
                  aria-labelledby="headingFilters"
                  data-bs-parent="#mainAccordion"
                >
                  <div className="accordion-body">
                    <div className="mb-3">
                      <label>Category:</label>
                      <select
                        className="form-select"
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                      >
                        <option value="">All</option>
                        <option value="Self-build">Self-build</option>
                        <option value="Colocation">Colocation</option>
                      </select>
                    </div>
                    {/* New "By Company" Filter */}
                    <div className="mb-3">
                      <label>By Company:</label>

                      <div style={{ position: "relative", width: "100%" }}>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search for a company"
                          value={companySearch}
                          onChange={handleCompanySearch}
                          style={{ paddingRight: "30px" }} // leave room for icon inside input
                        />

                        {companySearch && (
                          <img
                            src="/icons/cancel.png"
                            alt="Clear"
                            onClick={() => {
                              setCompanySearch("");
                              setSelectedCompany(null);
                              setCompanySuggestions([]);
                            }}
                            style={{
                              position: "absolute",
                              top: "50%",
                              right: "10px",
                              transform: "translateY(-50%)",
                              height: "16px",
                              width: "16px",
                              cursor: "pointer",
                              opacity: 0.6,
                            }}
                          />
                        )}
                      </div>

                      {companySuggestions.length > 0 && (
                        <ul
                          className="list-group position-fixed mt-2 overflow-auto"
                          style={{
                            zIndex: 10000,
                            maxHeight: "250px",
                            width: "12%",
                            border: "1px solid #ccc",
                            backgroundColor: "#fff",
                            borderRadius: "4px",
                            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          {companySuggestions.map((company, index) => (
                            <li
                              key={index}
                              className="list-group-item"
                              onClick={() => handleCompanySelect(company)}
                              style={{ cursor: "pointer" }}
                            >
                              {company}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>



                    {/* Size Filters */}
                    <>
                      {["size_live", "size_under_construction", "size_design"].map((sizeType) => (
                        <div className="mb-3" key={sizeType}>
                          <label>{sizeType.replace(/_/g, " ").replace("size", "Size")} (MW):</label>
                          <div className="row">
                            <div className="col">
                              <input
                                type="number"
                                className="form-control"
                                placeholder="Min"
                                value={filterSize[sizeType].min}
                                onChange={(e) =>
                                  setFilterSize((prev) => ({
                                    ...prev,
                                    [sizeType]: { ...prev[sizeType], min: e.target.value },
                                  }))
                                }
                              />
                            </div>
                            <div className="col">
                              <input
                                type="number"
                                className="form-control"
                                placeholder="Max"
                                value={filterSize[sizeType].max ?? ""}
                                onChange={(e) =>
                                  setFilterSize((prev) => ({
                                    ...prev,
                                    [sizeType]: { ...prev[sizeType], max: e.target.value },
                                  }))
                                }
                              />
                            </div>
                          </div>
                        </div>

                      ))}
                      <button style={{"width": "100%", "border": "1px solid black", "borderRadius": "5px"}} onClick={() => {
                        setFilterCategory(initialState.filterCategory);
                        setFilterSize(initialState.filterSize);
                        setCompanySearch(initialState.searchQuery);
                      }}>Clear Filters</button>
                    </>
                  </div>
                </div>
              </div>


              <div className="accordion-item">
                <h2 className="accordion-header" id="headingDraw">
                  <button
                    className="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseDraw"
                    aria-expanded="false"
                    aria-controls="collapseDraw"
                  >
                    Draw
                  </button>
                </h2>
                <div
                  id="collapseDraw"
                  className="accordion-collapse collapse"
                  aria-labelledby="headingDraw"
                  data-bs-parent="#mainAccordion"
                >
                  <div className="accordion-body">
                    <p>Select a shape and draw on the map:</p>
                    <div className="mb-3">
                      <button
                        className="btn btn-outline-primary w-100 mb-2"
                        onClick={() => enableDrawing("circle")}
                      >
                        Circle
                      </button>
                      <button
                        className="btn btn-outline-primary w-100 mb-2"
                        onClick={() => enableDrawing("rectangle")}
                      >
                        Rectangle
                      </button>
                      <button
                        className="btn btn-outline-primary w-100 mb-3"
                        onClick={() => enableDrawing("polygon")}
                      >
                        Free Form
                      </button>

                      <hr />

                      <button
                        className="btn btn-outline-success w-100 mb-2"
                        onClick={() => enableMeasurement("polygon")}
                      >
                        Measure Area
                      </button>
                      <button
                        className="btn btn-outline-success w-100 mb-2"
                        onClick={() => enableMeasurement("polyline")}
                      >
                        Measure Distance
                      </button>

                      <div
                        className="d-flex justify-content-center align-items-center mt-3"
                        onClick={removeDrawnShapes}
                        style={{ cursor: "pointer" }}
                      >
                        <i
                          className="fas fa-trash"
                          style={{ fontSize: "24px", color: "red" }}
                          title="Clear Shapes"
                        ></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="position-absolute"
              style={{
                bottom: '10px', // Adjust the space from the bottom
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1, // Lower z-index for the logo
              }}
            >
              <img
                src="/logo.png"
                alt="Logo"
                className="img-fluid"
                style={{ maxWidth: '70px' }}
              />
            </div>
          </aside>

          {showZoomHint && (
            <div className="zoom-hint-toast">
              Please zoom in to view datapoints.
            </div>
          )}


          <main className="col-md-9 col-lg-10 p-0">
            {!mapsLoaded && !mapsError && (
              <div style={{ padding: 16 }}>Loading map...</div>
            )}
            {mapsError && (
              <div role="alert" style={{ padding: 16, color: "#b00020" }}>
                Error loading Google Maps: {mapsError.message}
              </div>
            )}

            {mapsLoaded && (
              <GoogleMap
                key={mapKey} // Use the dynamic key
                onClick={() => {
                  setSelectedCentre(null);           // Deselect map-clicked data centre
                  setSearchSelectedCentre(null);     // Deselect search result InfoWindow
                  setSelectedPlant(null);            // Deselect power plant
                  setSelectedSubstation(null);       // Deselect substation
                  setSelectedLandingStation(null);
                  setSelectedAirport(null);
                  setSelectedInternetExchange(null);
                  setSelectedPowerLine(null);
                  setSelectedShape(null);            // Deselect any drawn shape
                }}

                mapContainerStyle={mapContainerStyle}
                center={userLocation}
                zoom={zoomLevel}
                onLoad={onLoad}
                onIdle={onMapIdle}
                options={{
                  minZoom: 2.5,  // Minimum zoom level for Google Maps
                  maxZoom: 24, // Maximum zoom level for Google Maps (varies by location and imagery)
                  restriction: {
                    latLngBounds: {
                      north: 85,
                      south: -85,
                      west: -180,
                      east: 180,
                    },
                    strictBounds: false, // Set to true to lock inside bounds completely
                  },
                }}
              >


                {visibleLayers.dataCentres &&
                  filteredDataCentres.
                    map((centre) => {
                      // Determine the icon based on the category
                      const iconUrl =
                        centre.category === 'Colocation'
                          ? 'icons/datacentre_colocation_2.png' // Icon for colocation
                          : 'icons/datacentre_selfbuild.png'; // Icon for self-build

                      return (
                        <Marker
                          key={`datacentre-${centre.id}`}
                          position={{
                            lat: Math.round(Number(centre.latitude) * 1e6) / 1e6,
                            lng: Math.round(Number(centre.longitude) * 1e6) / 1e6,
                          }}
                          icon={{
                            url: iconUrl, // Dynamically set icon based on category
                            scaledSize: new window.google.maps.Size(20, 20), // Adjust size if needed
                          }}
                          onClick={() => {
                            setSelectedCentre(centre);
                            setSearchSelectedCentre(null)
                          }}
                          zIndex={centre.id}
                        />

                      );
                    })}


                {visibleLayers.airports &&
                  layers.airports &&
                  layers.airports.map((airport) => (
                    <Marker
                      key={`airport-${airport.id}`}
                      position={{
                        lat: Math.round(parseFloat(airport.latitude) * 1e6) / 1e6,
                        lng: Math.round(parseFloat(airport.longitude) * 1e6) / 1e6,
                      }}
                      icon={{
                        url: '/icons/airport.png', // Icon for airports
                        scaledSize: new window.google.maps.Size(32, 32),
                      }}
                      onClick={() => {
                        setSelectedAirport(airport);
                      }}
                      zIndex={airport.id}
                    />
                  ))}

                {visibleLayers.landingstations &&
                  layers.landingStations &&
                  layers.landingStations.map((station) => (
                    <Marker
                      key={`landing-station-${station.id}`}
                      position={{
                        lat: Math.round(parseFloat(station.latitude) * 1e6) / 1e6,
                        lng: Math.round(parseFloat(station.longitude) * 1e6) / 1e6,
                      }}
                      icon={{
                        url: '/icons/structure.png', // Icon for landing stations
                        scaledSize: new window.google.maps.Size(22, 22),
                      }}
                      onClick={() => {
                        setSelectedLandingStation(station);
                      }}
                      zIndex={station.id}
                    />
                  ))}

                {visibleLayers.internetExchanges &&
                  layers.internetExchanges &&
                  layers.internetExchanges.map((exchange) => (
                    <Marker
                      key={`internet-exchange-${exchange.id}`}
                      position={{
                        lat: Math.round(parseFloat(exchange.latitude) * 1e6) / 1e6,
                        lng: Math.round(parseFloat(exchange.longitude) * 1e6) / 1e6,
                      }}
                      icon={{
                        url: '/icons/exchange.png', // Icon for internet exchanges
                        scaledSize: new window.google.maps.Size(30, 30),
                      }}
                      onClick={() => {
                        setSelectedInternetExchange(exchange);
                      }}
                      zIndex={exchange.id}
                    />
                  ))}

                {visibleLayers.powerPlants &&
                  layers.powerPlants &&
                  layers.powerPlants.map((plantGroup, index) => {
                    const isAggregated = plantGroup.plant_count > 1;

                    if (isAggregated) {
                      // Render aggregated marker
                      return (
                        <Marker
                          key={`powerplant-group-${index}`}
                          position={{
                            lat: parseFloat(plantGroup.latitude),
                            lng: parseFloat(plantGroup.longitude),
                          }}
                          icon={{
                            url: '/icons/circle_plant.png',
                            labelOrigin: new window.google.maps.Point(14, 14),
                            scaledSize: new window.google.maps.Size(28, 28),
                          }}
                          label={{
                            text: `${plantGroup.plant_count}`,
                            fontSize: '10px',
                            fontWeight: 'bold',
                            color: 'white',
                          }}
                          onClick={async () => {
                            try {
                              const data = await fetchPlantDetails(plantGroup.plant_ids);

                              const detailedGroups = data.map((plant) => ({
                                ...plant,
                                latitude: plant.latitude,
                                longitude: plant.longitude,
                                plant_count: 1,
                                plant_ids: [plant.id],
                              }));

                              setLayers((prev) => ({
                                ...prev,
                                powerPlants: prev.powerPlants
                                  .filter(
                                    (group) =>
                                      group.plant_ids?.toString() !== plantGroup.plant_ids?.toString()
                                  )
                                  .concat(detailedGroups),
                              }));
                            } catch (err) {
                              console.error("Error expanding group:", err);
                            }
                          }}

                        />
                      );
                    } else {
                      // Render individual marker
                      return (
                        <Marker
                          key={`powerplant-${index}`}
                          position={{
                            lat: parseFloat(plantGroup.latitude),
                            lng: parseFloat(plantGroup.longitude),
                          }}
                          icon={{
                            url: '/icons/powerplant.png',
                            scaledSize: new window.google.maps.Size(28, 28),
                          }}
                          onClick={async () => {
                            try {
                              const data = await fetchPlantDetails(plantGroup.plant_ids);

                              if (data.length > 0) {
                                setSelectedPlant(data[0]);
                              } else {
                                console.warn('No data returned for plant ID:', plantGroup.plant_ids);
                              }
                            } catch (err) {
                              // Error already logged in helper
                            }
                          }}
                        />
                      );
                    }
                  })
                }

                {visibleLayers.substations &&
                  layers.substations &&
                  layers.substations.map((substationGroup, index) => {
                    const isAggregated = Number(substationGroup.substation_count) > 1;

                    if (isAggregated) {
                      return (
                        <Marker
                          key={`substation-group-${index}`}
                          position={{
                            lat: parseFloat(substationGroup.latitude),
                            lng: parseFloat(substationGroup.longitude),
                          }}
                          icon={{
                            url: '/icons/circle_substation.png',
                            labelOrigin: new window.google.maps.Point(14, 14),
                            scaledSize: new window.google.maps.Size(28, 28),
                          }}
                          label={{
                            text: `${substationGroup.substation_count}`,
                            fontSize: "9px",
                            fontWeight: "bold",
                            color: "white",
                          }}
                          onClick={async () => {
                            try {
                              const data = await fetchSubstationDetails(substationGroup.substation_ids);

                              const detailedGroups = data.map((substation) => ({
                                ...substation,
                                latitude: substation.latitude,
                                longitude: substation.longitude,
                                substation_count: 1,
                                substation_ids: [substation.id],
                              }));

                              setLayers((prev) => ({
                                ...prev,
                                substations: prev.substations
                                  .filter(
                                    (group) =>
                                      group.substation_ids?.toString() !==
                                      substationGroup.substation_ids?.toString()
                                  )
                                  .concat(detailedGroups),
                              }));
                            } catch (error) {
                              console.error("Error expanding substation group:", error);
                            }
                          }}
                        />
                      );
                    } else {
                      return (
                        <Marker
                          key={`individual-substation-${index}`}
                          position={{
                            lat: parseFloat(substationGroup.latitude),
                            lng: parseFloat(substationGroup.longitude),
                          }}
                          icon={{
                            url: "/icons/substation.png",
                            scaledSize: new window.google.maps.Size(22, 22),
                          }}
                          onClick={async () => {
                            try {
                              const data = await fetchSubstationDetails(substationGroup.substation_ids);
                              if (data.length > 0) {
                                setSelectedSubstation(data[0]);
                              }
                            } catch (error) {
                              console.error("Error fetching substation:", error);
                            }
                          }}
                          zIndex={index}
                        />
                      );
                    }
                  })}


                {visibleLayers.powerLines &&
                  Array.isArray(layers.powerLines) &&
                  layers.powerLines.map((line) => {
                    // Colour rule: >130 kV = orange; else yellow
                    const kv = Number(line.voltage_kv);
                    const strokeColor = Number.isFinite(kv) && kv > 140 ? '#a80a17' : '#ab520e';

                    return (
                      <Polyline
                        key={`powerline-${line.key}`}
                        path={line.path}
                        options={{
                          strokeColor,
                          strokeOpacity: 0.95,
                          strokeWeight: 2, // thinner than defaults
                        }}
                        onClick={async (e) => {
                          // Lazy-load full details only when clicked (your existing helper)
                          const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
                          setPowerLineLoading(true);
                          setSelectedPowerLine({ id: line.id, position: pos });

                          const detail = await fetchPowerLineDetails(line.id);
                          setSelectedPowerLine(detail ? { ...detail, position: pos } : { id: line.id, position: pos });
                          setPowerLineLoading(false);
                        }}
                      />
                    );
                  })
                }


                {visibleLayers.tempMasks && mapRef.current && (
                  <>
                    <GroundOverlay
                      map={mapRef.current}
                      url="/temp_masks/SAU.png"
                      bounds={saudiBounds}
                      opacity={0.6}
                    />
                    <GroundOverlay
                      map={mapRef.current}
                      url="/temp_masks/GBR.png"
                      bounds={ukBounds}
                      opacity={0.6}
                    />
                  </>
                )}



                {searchSelectedCentre && (
                  <>
                    <Marker
                      position={{
                        lat: parseFloat(searchSelectedCentre.latitude),
                        lng: parseFloat(searchSelectedCentre.longitude),
                      }}
                      icon={{
                        url: "icons/search_result.png",
                        scaledSize: new window.google.maps.Size(20, 20),
                      }}
                      onClick={() => setSearchSelectedCentre(searchSelectedCentre)}
                    />
                    <SearchInfoWindow
                      centre={searchSelectedCentre}
                      onClose={() => setSearchSelectedCentre(null)}
                    />
                  </>
                )}


                {selectedCentre && (
                  <InfoWindow
                    position={{
                      lat: parseFloat(selectedCentre.latitude),
                      lng: parseFloat(selectedCentre.longitude),
                    }}
                    onCloseClick={() => setSelectedCentre(null)}
                    options={{ zIndex: 1000 }}
                  >
                    <div>
                      <h5>
                        {(selectedCentre.company ? selectedCentre.company + " - " : "") +
                          (selectedCentre.sitename || selectedCentre.name)}
                      </h5>
                      <ul>
                        {selectedCentre.sitename && (
                          <li><strong>Site Name:</strong> {selectedCentre.sitename}</li>
                        )}
                        {selectedCentre.company && (
                          <li><strong>Company:</strong> {selectedCentre.company}</li>
                        )}
                        {selectedCentre.category && (
                          <li><strong>Category:</strong> {selectedCentre.category}</li>
                        )}

                        {selectedCentre.country && (
                          <li><strong>Country:</strong> {selectedCentre.country}</li>
                        )}
                        {selectedCentre.size_l && (
                          <li><strong>Size (Live):</strong> {`${selectedCentre.size_l} MW`}</li>
                        )}
                        {selectedCentre.size_u && (
                          <li><strong>Size (Under Construction):</strong> {`${selectedCentre.size_u} MW`}</li>
                        )}
                        {selectedCentre.size_d && (
                          <li><strong>Size (Design):</strong> {`${selectedCentre.size_d} MW`}</li>
                        )}
                      </ul>

                    </div>
                  </InfoWindow>


                )}


                {selectedLandingStation && (
                  <InfoWindow
                    position={{
                      lat: parseFloat(selectedLandingStation.latitude),
                      lng: parseFloat(selectedLandingStation.longitude),
                    }}
                    onCloseClick={() => setSelectedLandingStation(null)}
                    options={{ zIndex: 1000 }}
                  >
                    <div>
                      <h6>{selectedLandingStation.name || "Landing Station"}</h6>
                      {selectedLandingStation.cables && (
                        <div>
                          <strong>Cables:</strong>
                          <ul>
                            {selectedLandingStation.cables
                              .split(',')
                              .map((c) => c.trim())
                              .filter(Boolean)
                              .map((cable, idx) => (
                                <li key={idx}>{cable}</li>
                              ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </InfoWindow>
                )}

                {selectedInternetExchange && (
                  <InfoWindow
                    position={{
                      lat: parseFloat(selectedInternetExchange.latitude),
                      lng: parseFloat(selectedInternetExchange.longitude),
                    }}
                    onCloseClick={() => setSelectedInternetExchange(null)}
                    options={{ zIndex: 1000 }}
                  >
                    <div>
                      <ul>
                        <li><strong>Building Name:</strong> {selectedInternetExchange.building_name || "N/A"}</li>
                        <li><strong>Exchange Names:</strong>{selectedInternetExchange.exchange_names || "N/A"}</li>
                      </ul>
                    </div>
                  </InfoWindow>
                )}

                {selectedAirport && (
                  <InfoWindow
                    position={{
                      lat: parseFloat(selectedAirport.latitude),
                      lng: parseFloat(selectedAirport.longitude),
                    }}
                    onCloseClick={() => setSelectedAirport(null)}
                    options={{ zIndex: 1000 }}
                  >
                    <div>
                      <h5>{selectedAirport.name || "Airport"}</h5>
                      <ul>
                        <li><strong>Type:</strong> {selectedAirport.type || "N/A"}</li>
                      </ul>
                    </div>
                  </InfoWindow>
                )}

                {selectedSubstation && (
                  <InfoWindow
                    position={{
                      lat: parseFloat(selectedSubstation.latitude),
                      lng: parseFloat(selectedSubstation.longitude),
                    }}
                    onCloseClick={() => setSelectedSubstation(null)} // Clear the selected substation on close
                  >
                    <div>
                      <h5>{selectedSubstation.name || "Substation"}</h5>
                      <ul>
                        {selectedSubstation.operator && (
                          <li>
                            <strong>Operator:</strong> {selectedSubstation.operator}
                          </li>
                        )}
                        {selectedSubstation.substation && (
                          <li>
                            <strong>Type:</strong> {selectedSubstation.substation}
                          </li>
                        )}
                        {selectedSubstation.voltage && (
                          <li>
                            <strong>Voltage:</strong> {selectedSubstation.voltage} V
                          </li>
                        )}
                      </ul>

                    </div>
                  </InfoWindow>
                )}

                {selectedPlant && (
                  <InfoWindow
                    position={{
                      lat: parseFloat(selectedPlant.latitude),
                      lng: parseFloat(selectedPlant.longitude),
                    }}
                    onCloseClick={() => setSelectedPlant(null)} // Close the InfoWindow when clicked
                  >
                    <div>
                      <h5>{selectedPlant.name || "Power Plant"}</h5>
                      <ul>

                        {selectedPlant.primary_fuel && (
                          <li>
                            <strong>Source:</strong> {selectedPlant.primary_fuel}
                          </li>
                        )}
                        {selectedPlant.capacity_mw !== undefined && (
                          <li>
                            <strong>Capacity:</strong> {Number(selectedPlant.capacity_mw).toFixed(2)} MW
                          </li>
                        )}

                      </ul>
                    </div>

                  </InfoWindow>
                )}

                {selectedPowerLine && (
                  <InfoWindow
                    position={selectedPowerLine.position}
                    onCloseClick={() => setSelectedPowerLine(null)}
                    options={{ zIndex: 1000 }}
                  >
                    <div>
                      <h5>{selectedPowerLine.name}</h5>
                      <ul>
                        {selectedPowerLine.operator && <li><strong>Operator:</strong> {selectedPowerLine.operator}</li>}
                        {selectedPowerLine.voltage_kv != null && <li><strong>Voltage:</strong> {selectedPowerLine.voltage_kv} kV</li>}
                        {selectedPowerLine.circuits != null && <li><strong>Circuits:</strong> {selectedPowerLine.circuits}</li>}
                        {selectedPowerLine.status && <li><strong>Status:</strong> {selectedPowerLine.status}</li>}
                        {selectedPowerLine.length_km != null && <li><strong>Length:</strong> {selectedPowerLine.length_km} km</li>}
                        {(selectedPowerLine.from_substation || selectedPowerLine.to_substation) && (
                          <li>
                            <strong>Endpoints:</strong>
                            {' '}
                            {selectedPowerLine.from_substation ?? 'Unknown'}
                            {' '}
                            →
                            {' '}
                            {selectedPowerLine.to_substation ?? 'Unknown'}
                          </li>
                        )}
                      </ul>
                    </div>
                  </InfoWindow>
                )}


                {selectedShape && (
                  <InfoWindow
                    position={selectedShape.position}
                    onCloseClick={() => {
                      if (selectedShape.shape) selectedShape.shape.setMap(null); // Remove shape
                      setSelectedShape(null); // Clear selected shape
                    }}
                  >
                    <div className="infowindow-scrollable">
                      {/* Loading Spinner */}
                      {loading ? (
                        <div style={{ textAlign: "center", padding: "2em" }}>
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p>Loading data...</p>
                        </div>
                      ) : (
                        <>
                          {/* Data Centres Section */}
                          {selectedShape.dataCentres && (
                            <>
                              <h5>Data Centres</h5>
                              <table className="table table-bordered">
                                <thead>
                                  <tr>
                                    <th>Category</th>
                                    <th>Count</th>
                                    <th>Live</th>
                                    <th>Under Construction</th>
                                    <th>Committed</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedShape.dataCentres.rows.map((row, index) => (
                                    <tr key={index}>
                                      <td>{row.category}</td>
                                      <td>{row.count}</td>
                                      <td>{Math.round(row.live * 100) / 100}</td>
                                      <td>{Math.round(row.underConstruction * 100) / 100}</td>
                                      <td>{Math.round(row.committed * 100) / 100}</td>
                                    </tr>
                                  ))}

                                </tbody>
                              </table>
                              <h6>Companies:</h6>
                              <ul>
                                {selectedShape.dataCentres.companies.map((company, index) => (
                                  <li key={index}>{company}</li>
                                ))}
                              </ul>
                            </>
                          )}

                          {/* Power Plants Section */}
                          {selectedShape.powerPlants && (
                            <>
                              <h5>Power Plants</h5>
                              <p>
                                <strong>Total Output:</strong> {Math.ceil(selectedShape.powerPlants.totalOutput)} MW
                              </p>
                              <p>
                                <strong>Number of Plants:</strong> {Math.ceil(selectedShape.powerPlants.numberOfPlants)}
                              </p>
                              <h6>Methods:</h6>
                              <ul>
                                {selectedShape.powerPlants.methods.map((method, index) => (
                                  <li key={index}>{method}</li>
                                ))}
                              </ul>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </InfoWindow>
                )}



              </GoogleMap>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default MapComponent;
